import { StockItemSearch, StockItemSearchResult, StockItemSearchService } from './../../acumatica/EntityServices/StockItemSearch.service';
/*
  Selector for Inventory Items

  <inventory-id-selector #itemSelector
    [inventoryID]="inventoryID"
    ------    [search]="true" // option to enable item description searching, default false
    (onSubmit)="valueEntered($event)"
    (onChange)="itemSelected($event)">
  </inventory-id-selector>

  inventoryID attribute may be bound to a string field for the ID
  
  onSubmit(SelectorEvent) is triggered when a value has been [Enter]ed, before an item is fetched
    the SelectorEvent contains the value entered in the value property
    the preventDefault proeprty may be set to true to disable fetching the ID from the server
    assign new values using the component reference .inventoryID property i.e.
          onSubmitHandler(event: SelectorEvent) {
            if (event.valuetrim().toUpperCase() == 'WC920') this.itemSelector.inventoryID = 'WC920CD';
          }

  
  onChange(StockItem | undefined) is triggered when an item is fetched from the server
    or undefined if no such item exists

  given a reference to the component element...
    @ViewChild('itemSelector', { static: true }) itemSelector!: InventoryIDSelectorComponent;
  .focus() event will set focus to the input field and select its contents
  .reset() event will set focus to the input element and clear the entered item / value

  .item property may be used to get the StockItem object reference

*/

import { MatDialog } from '@angular/material/dialog';
import { StockItem, StockItemService } from './../../acumatica/EntityServices/StockItem.service';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { Component, ElementRef, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { AlertDialog } from '../alert-dialog/alert-dialog.component';
import { Sound } from 'src/app/services/audio.service';
import { MayerFabricsService } from 'src/app/services/mayer-fabrics.service';

export class SelectorEvent {
  public value: string;
  public preventDefault: boolean = false;

  public constructor(value: string) {
    this.value = value;
  }
}

@Component({
  selector: 'inventory-id-selector',
  templateUrl: './inventory-id-selector.component.html',
  styleUrls: ['./inventory-id-selector.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: InventoryIDSelectorComponent,
      multi: true
    }
  ]
})
export class InventoryIDSelectorComponent {
  private _hasFocus: boolean = false;
  isSearching: boolean = false;

  @ViewChild('inventoryIDInput', { static: true }) inventoryIDInput!: ElementRef;
  @ViewChild('itemSearchDialogTemplate', { static: true }) itemSearchDialogTemplate!: TemplateRef<any>;
  @ViewChild('itemTreeSearchDialogTemplate', { static: true }) itemTreeSearchDialogTemplate!: TemplateRef<any>;
  private inputElement!: HTMLInputElement;

  private _inventoryID: string | null = null;
  @Input() get inventoryID(): string | null { return this._inventoryID; }
  set inventoryID(value: string | null) {
    // set inventoryID to null -- clear the item
    if (value === null || value === '' || value === undefined) {
      this._inventoryID = null;
      this.item = undefined;
      return;
    }

    value = value.trim().toUpperCase();

    // only when assigning a different inventoryID
    if (value != this._inventoryID) {
      this._inventoryID = value;
      if (!this._hasFocus) {
        // fetch the inventory item (or open search)
        setTimeout(() => { this.searchForItem(); }, 10);
      } else {
        // inventory ID is being changed while it has focus -- user typing
        // clear the item and wait for focus lost
        this._item = undefined;
      }
    }
  }

  @Input() search: boolean = false;
  @Input() searchWhse?: string;
  @Input() searchValidator?: (results: StockItemSearchResult[]) => StockItemSearchResult[];

  private _item?: StockItem;
  get item(): StockItem | undefined { return this._item; }
  set item(item: StockItem | undefined) {
    if (this._item?.InventoryID != this._inventoryID) {
      this._item = item;
      this._inventoryID = item?.InventoryID || null;
      if (!this._isResetting) this.onChange.emit(item);
    }
  }

  @Output() onChange = new EventEmitter<StockItem | undefined>();
  @Output() onSubmit = new EventEmitter<SelectorEvent>();

  constructor(
    private items: StockItemService,
    private itemSearch: StockItemSearchService,
    private dialog: MatDialog,
    public mayerfabrics: MayerFabricsService
  ) {
    setTimeout(() => {
      this.inputElement = <HTMLInputElement>this.inventoryIDInput.nativeElement!
    })
  }

  focus() {
    this.inventoryIDInput.nativeElement.focus();
    this.inventoryIDInput.nativeElement.select();
  }

  _isResetting = false;
  reset() {
    this._isResetting = true;
    this.inventoryID = null;
    this.inventoryIDInput.nativeElement.focus();
    this._isResetting = false;
  }

  onFocus() {
    this._hasFocus = true;
    if (this.item !== undefined) this._inventoryID = this.item.InventoryID;
    this.selectInputFieldValue();
  }

  private selectInputFieldValue() {
    setTimeout(() => { this.inputElement.focus(); this.inputElement.select(); });
  }

  onEnter() {
    // raise the onSubmit event indicating a value has been typed and [Enter] pressed
    let e = new SelectorEvent(this.inventoryID!);
    this.onSubmit.emit(e);
    if (e.preventDefault) return;

    // timeout to allow onSubmit event handlers to potentially change entered value
    // (remap mis-coded barcode values)
    setTimeout(() => {
      this.doBlur();
      this.searchForItem();
    }, 100);
  }

  doBlur() { this.inputElement.blur(); }

  onBlur() {
    this._hasFocus = false;
    if (this._inventoryID === this.item?.InventoryID) {
      this._inventoryID = this.item!.InventoryID + ' - ' + this.item!.Description;
    }
  }

  writeValue(value: string | null) {
    this.inventoryID = value;
  }

  _changeHandlers: any[] = [];
  registerOnChange(fn: any): void { this._changeHandlers.push(fn); }
  registerOnTouched(fn: any): void { }
  setDisabledState?(isDisabled: boolean): void { }

  itemSearchResults: StockItemSearchResult[] = [];
  searchForItem() {
    // set to null or empty string
    if (!this.inventoryID) {
      this.item = undefined;
      return;
    }
    this.isSearching = true;

    // try getting the stock item by the exact inventory id value provided
    this.items.get(this.inventoryID, { loader: null }).subscribe(item => {
      if (item) {
        this.isSearching = false;
        this.item = item;
        if (!this._hasFocus) this._inventoryID = item.InventoryID + ' - ' + item.Description;
      }
    }, err => {
      if (!this.search) {
        this.isSearching = false;
        this.onChange.emit(undefined);
        return;
      }

      // try doing an item search
      let inquiry = new StockItemSearch({ search: this.inventoryID });
      if (this.searchWhse !== undefined) inquiry.DefaultWarehouse = this.searchWhse;

      this.itemSearch.search(inquiry).subscribe(search => {
        this.isSearching = false;

        if (this.searchValidator) {
          search.Results = this.searchValidator(search.Results);
        }

        if (search.Results.length == 0) {
          this.dialog.open(AlertDialog, {
            data: {
              message: "Search returned no results",
              icon: 'warning',
              Sound: Sound.GentleBeep,
              buttonText: 'OK'
            }
          }).afterClosed().subscribe(() => { this.selectInputFieldValue() });
        } else {
          this.itemSearchResults = search.Results.sort((a, b) => a.Description.localeCompare(b.Description));

          this.dialog.open(this.itemSearchDialogTemplate).afterClosed().subscribe(selection => {
            this.inventoryID = selection.InventoryID;
            // this.searchForItem();
          });
        }
      })
    })
  }

  openItemSearch() {

  }
}
